/**
 * The main route (`111-frontend-architecture.md` §7): breadcrumb, title, the editor, and
 * the reconciliation that decides what the user sees on mount (`112-offline-drafts.md`
 * §7).
 *
 * The three things this file must get right, in order of how badly they hurt:
 *
 *  1. **The backend being down is not an error state.** If the page fetch fails but a
 *     draft exists, we render the DRAFT and keep accepting keystrokes. This is the whole
 *     of M2 acceptance criterion 8 — kill the backend, type, reload, and the sentence is
 *     still on screen.
 *  2. **A restored draft is silent.** No banner, no prompt, no "we recovered your work!"
 *     modal. The user opens the page and their text is simply THERE, exactly as they left
 *     it. That is what the guarantee should feel like.
 *  3. **A divergence is never resolved for the user.** Two versions, no right to pick one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { keys } from '@/api/keys';
import { emptyDoc, getPage, type PageDto, type ProseMirrorDoc } from '@/api/endpoints/pages';
import { deepEqual } from '@/lib/content';
import { cn } from '@/lib/utils';
import { ulid } from '@/lib/ulid';
import { translate, useTranslation } from '@/lib/i18n';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { DueDateButton } from '@/features/calendar/DueDate';
import { EventLinksPanel } from '@/features/calendar/EventLinksPanel';
import { Editor } from '@/features/editor/Editor';
import { RecoveryBanner } from '@/features/editor/RecoveryBanner';
import type { SaveStateKind } from '@/features/editor/SaveState';
import { TagInput } from '@/features/tags/TagInput';
import { BacklinksPanel } from '@/features/notebook/BacklinksPanel';
import { appendPosition, childrenOf } from '@/features/notebook/treeModel';
import {
  useCreatePage,
  usePage,
  useTree,
  useUpdatePage,
} from '@/features/notebook/usePageMutations';
import { openConflict, resolveConflict } from '@/offline/conflicts';
import type { DraftRecord } from '@/offline/db';
import {
  backupDraft,
  backupServerVersion,
  deleteDraft,
  loadDraft,
  reconcile,
  restoreBackup,
  saveDraft,
} from '@/offline/drafts';
import { outbox } from '@/offline/outbox';
import { useConflict, useSyncStatus } from '@/offline/useSyncStatus';

interface Boot {
  content: ProseMirrorDoc;
  /** The version the editor's document is based on — the next save's precondition. */
  baseUpdatedAt: string;
  title: string;
  /** True when we are showing a draft because the server could not be reached. */
  offline: boolean;
  /** Bumped on a conflict resolution to remount the editor with the chosen document. */
  revision: number;
}

/**
 * The 10-second `[Undo]` after "Use server version" — the only path in the app that
 * discards local content, so it is the one path that gets a way back.
 */
function offerUndo(onUndo: () => void): void {
  toast(translate('notebook.switchedToServer'), {
    description: translate('notebook.switchedToServerDescription'),
    duration: 10_000,
    action: { label: translate('notebook.undo'), onClick: onUndo },
  });
}

/** Enqueue the restored draft immediately, so it lands the moment the server is reachable. */
function flushDraft(draft: DraftRecord): void {
  void outbox.enqueue({
    kind: 'page.update',
    entityId: draft.pageId,
    label: draft.title,
    request: {
      method: 'PATCH',
      path: `/api/pages/${draft.pageId}`,
      body: { content: draft.content },
      ifUnmodifiedSince: draft.baseUpdatedAt,
    },
  });
}

export function PageView() {
  const { pageId } = useParams<{ pageId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Set by the tree when a page was just created and named (`060` §6): the caret belongs in
  // the body, so the user can keep typing without reaching for the mouse.
  const focusEditor = (location.state as { focusEditor?: boolean } | null)?.focusEditor === true;

  const page = usePage(pageId);
  const tree = useTree();
  const update = useUpdatePage();
  const { mutate: createPage } = useCreatePage();
  const sync = useSyncStatus();
  const conflict = useConflict(pageId);

  /**
   * Create the page a `[[…]]` was waiting for — from the backlinks panel's "Create" button,
   * or by clicking the broken link itself.
   *
   * **As a child of the current page**, because "that is almost always the right home for
   * `[[The Grey Vault]]` written on `Ithkar`" (`070` §7.6).
   *
   * Nothing here resolves the link. The `page.create` goes through the outbox; the server's
   * `PageLinkResolver` fills in `page_link.target_page_id` for every unresolved row whose
   * `target_title` matches, in the same transaction (`070` §7.4); and the wiki-link node —
   * still carrying `pageId: null`, still un-rewritten — resolves at RENDER time against the
   * tree we are about to invalidate. Three mechanisms, one create, no document touched.
   */
  const createLinkedPage = useCallback(
    (title: string) => {
      if (pageId === undefined) return;

      const id = ulid();
      const pages = tree.data?.pages ?? [];
      createPage({ id, parentId: pageId, position: appendPosition(pages, pageId), title });
      toast.success(t('notebook.createdToast', { title }));
    },
    [createPage, pageId, t, tree.data],
  );

  /**
   * The tree/fetch half of "Gather sub-pages" (`061` §2). Resolving children from the flat
   * tree and fetching their bodies is a notebook concern — the editor may not reach into
   * `features/notebook` (`111` §2) — so it lives here and is handed to the editor, which owns
   * the document-building and the save.
   *
   * Direct children only, in tree order (`childrenOf` returns them position-sorted).
   * `fetchQuery` populates the detail cache as a side benefit, so opening a gathered child
   * afterwards is instant.
   */
  const gatherChildren = useCallback((): Promise<PageDto[]> => {
    if (pageId === undefined) return Promise.resolve([]);
    const children = childrenOf(tree.data?.pages ?? []).get(pageId) ?? [];
    return Promise.all(
      children.map((child) =>
        queryClient.fetchQuery({
          queryKey: keys.pages.detail(child.id),
          queryFn: () => getPage(child.id),
        }),
      ),
    );
  }, [pageId, queryClient, tree.data]);

  const [boot, setBoot] = useState<Boot | null>(null);
  const bootedFor = useRef<string | null>(null);
  const [bootPageId, setBootPageId] = useState<string | undefined>(pageId);

  /**
   * A new page id means a new document, and `boot` still holds the PREVIOUS page's.
   * Reset it during RENDER, not in an effect.
   *
   * Doing it in an effect leaves exactly one committed frame in which `pageId` is already
   * the new page but `boot` is still the old one — and since the editor is keyed on
   * `pageId`, that frame MOUNTS AN EDITOR UNDER THE NEW PAGE'S KEY HOLDING THE OLD PAGE'S
   * CONTENT. Two ways that bites, and both were seen in the wild (~3 runs in 8):
   *
   *   - You create a page, the tree navigates, and you type into an editor instance that is
   *     about to be thrown away. The keystrokes go nowhere.
   *   - Worse: if an autosave fired from that instance, it would write the OLD page's
   *     document to the NEW page's id.
   *
   * Setting state during render of the same component is React's supported escape hatch for
   * exactly this — it re-renders immediately and the bad frame is never committed.
   */
  if (bootPageId !== pageId) {
    setBootPageId(pageId);
    bootedFor.current = null;
    setBoot(null);
  }

  const server: PageDto | undefined = page.data;

  /* ── Reconciliation, once per page (§7) ──────────────────────────────────── */

  useEffect(() => {
    if (pageId === undefined) return;

    // Once per page id. A refetch (or our own save writing the detail cache) must NOT
    // re-boot the editor — that would remount it under the user's cursor mid-sentence.
    if (bootedFor.current === pageId) return;

    let cancelled = false;

    const settle = (next: Boot): void => {
      if (cancelled) return;
      bootedFor.current = pageId;
      setBoot(next);
    };

    if (server !== undefined) {
      void reconcile(server).then((result) => {
        switch (result.action) {
          case 'restore':
            // The user typed, the tab died, the save never landed. Nobody else has written
            // to the page since. Their draft strictly descends from the server's copy and
            // is strictly newer. Restore it — and flush it, so it lands with NO user
            // action (§7.1).
            settle({
              content: result.draft.content,
              baseUpdatedAt: result.draft.baseUpdatedAt,
              title: result.draft.title,
              offline: false,
              revision: 0,
            });
            flushDraft(result.draft);
            return;

          case 'conflict':
            // Two divergent versions and no right to pick one. Show the SERVER version,
            // read-only, under a banner that does not go away until a human decides.
            openConflict({ pageId, draft: result.draft, server: result.server });
            settle({
              content: result.server.content,
              baseUpdatedAt: result.server.updatedAt,
              title: result.server.title,
              offline: false,
              revision: 0,
            });
            return;

          case 'none':
          case 'discard':
          default:
            settle({
              content: server.content,
              baseUpdatedAt: server.updatedAt,
              title: server.title,
              offline: false,
              revision: 0,
            });
            return;
        }
      });
      return () => {
        cancelled = true;
      };
    }

    // The fetch failed. That is not the end of the story: the words may be right here.
    if (page.isError) {
      void loadDraft(pageId).then((draft) => {
        if (draft === undefined) return; // genuinely nothing to show; the error state renders
        settle({
          content: draft.content,
          baseUpdatedAt: draft.baseUpdatedAt,
          title: draft.title,
          offline: true,
          revision: 0,
        });
      });
    }

    return () => {
      cancelled = true;
    };
  }, [page.isError, pageId, server]);

  // (The boot reset itself happens during render — see above. This only records where we are.)
  useEffect(() => {
    if (pageId !== undefined) localStorage.setItem('rn.lastPage', pageId);
  }, [pageId]);

  /**
   * A conflict raised by a **412 replay** (§7.4) arrives while the editor is already mounted
   * and showing the user's own text. §7.3 requires that the document under an open banner be
   * the SERVER's version, read-only: "showing them an editable document that is about to be
   * replaced is how you get a second conflict on top of the first."
   *
   * Swapping it is safe precisely because nothing here is the only copy of anything — the
   * local version is in the IndexedDB draft, on the still-queued outbox entry, and on
   * `conflict.draft`, which is what **Keep mine** puts straight back.
   *
   * (On the reload path the boot already chose the server version, so this is a no-op there
   * — hence the `deepEqual` guard, which is also what stops it looping.)
   */
  useEffect(() => {
    if (conflict === undefined) return;

    setBoot((previous) => {
      if (previous === null || deepEqual(previous.content, conflict.server.content)) {
        return previous;
      }
      return {
        content: conflict.server.content,
        baseUpdatedAt: conflict.server.updatedAt,
        title: conflict.server.title,
        offline: false,
        revision: previous.revision + 1,
      };
    });
  }, [conflict]);

  /* ── Conflict resolution (§7.3) ──────────────────────────────────────────── */

  const keepMine = useCallback(() => {
    if (conflict === undefined || pageId === undefined) return;
    const { draft, server: theirs } = conflict;

    void (async () => {
      // Before overwriting, the SERVER version is backed up — so even the losing side of
      // the conflict survives. Nothing here is unrecoverable.
      await backupServerVersion(theirs);

      // We have now SEEN the server's version and are deliberately overwriting it, so the
      // draft is re-based onto it. This is the one place `baseUpdatedAt` may advance
      // without a save having landed, and it is a human's explicit decision.
      const rebased: DraftRecord = { ...draft, baseUpdatedAt: theirs.updatedAt };
      await saveDraft(rebased);

      // The detail cache must agree, or `baseUpdatedAt` above would keep handing the
      // editor the very version we just resolved away from.
      queryClient.setQueryData(keys.pages.detail(pageId), theirs);

      // The failed 412 entry is replaced, not retried: same content, fresh precondition.
      await outbox.discardPageUpdates(pageId);
      flushDraft(rebased);

      resolveConflict(pageId);
      setBoot((previous) => ({
        content: draft.content,
        baseUpdatedAt: theirs.updatedAt,
        title: draft.title,
        offline: false,
        revision: (previous?.revision ?? 0) + 1,
      }));
    })();
  }, [conflict, pageId, queryClient]);

  const useServer = useCallback(() => {
    if (conflict === undefined || pageId === undefined) return;
    const { draft, server: theirs } = conflict;

    void (async () => {
      // The ONLY path that discards local content — so it is the one path that keeps a way
      // back. The draft is backed up first, and the toast offers Undo for 10 seconds.
      await backupDraft(draft);
      await deleteDraft(pageId);
      await outbox.discardPageUpdates(pageId);

      queryClient.setQueryData(keys.pages.detail(pageId), theirs);
      resolveConflict(pageId);

      setBoot((previous) => ({
        content: theirs.content,
        baseUpdatedAt: theirs.updatedAt,
        title: theirs.title,
        offline: false,
        revision: (previous?.revision ?? 0) + 1,
      }));

      offerUndo(() => {
        void (async () => {
          const restored = await restoreBackup(pageId);
          if (restored === undefined) return;

          const rebased: DraftRecord = { ...restored, baseUpdatedAt: theirs.updatedAt };
          await saveDraft(rebased);
          flushDraft(rebased);

          setBoot((previous) => ({
            content: rebased.content,
            baseUpdatedAt: theirs.updatedAt,
            title: rebased.title,
            offline: false,
            revision: (previous?.revision ?? 0) + 1,
          }));
        })();
      });
    })();
  }, [conflict, pageId, queryClient]);

  /* ── The save indicator (`061` §8) ───────────────────────────────────────── */

  const save: SaveStateKind =
    conflict !== undefined || sync.kind === 'conflict'
      ? 'conflict'
      : sync.kind === 'offline'
        ? 'local' // "Saved locally" — reassuring, because it IS safe
        : sync.kind === 'saving'
          ? 'saving'
          : 'saved';

  /* ── Render ──────────────────────────────────────────────────────────────── */

  if (pageId === undefined) return null;

  if (boot === null) {
    if (page.isError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="font-medium">{t('notebook.couldntOpen')}</p>
          <p className="text-[var(--muted-foreground)]">{page.error.message}</p>
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-[78ch] space-y-3 p-6">
        <Skeleton className="h-7 w-1/3" />
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  }

  const title = server?.title ?? boot.title;
  const archived = server?.isArchived ?? false;
  const editable = conflict === undefined && !archived;
  const favorite = server?.isFavorite ?? false;

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 px-6 pt-4">
        {server !== undefined && server.breadcrumb.length > 0 && (
          <nav
            aria-label={t('notebook.breadcrumbLabel')}
            className="mb-1 flex flex-wrap items-center gap-1 text-xs text-[var(--muted-foreground)]"
          >
            {server.breadcrumb.map((crumb) => (
              <span key={crumb.id} className="flex items-center gap-1">
                <Link to={`/p/${crumb.id}`} className="hover:underline">
                  {crumb.title}
                </Link>
                <span aria-hidden>/</span>
              </span>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-1">
          <input
            aria-label={t('notebook.pageTitleLabel')}
            data-testid="page-title"
            value={title}
            readOnly={!editable}
            placeholder={t('notebook.untitled')}
            onChange={(event) => {
              // The tree and the header edit the same field and stay in sync through the
              // cache. Optimistic, coalesced by the outbox into one PATCH.
              update.mutate({ id: pageId, title: event.target.value });
            }}
            // NO `outline-none` here (M7, criterion 9). The title field is chromeless by design —
            // it should read as the page's heading, not as a form control — but "chromeless" is
            // about its RESTING state. Tailwind v4's `outline-none` also sets
            // `--tw-outline-style: none`, which poisons every later `outline-*` utility, and the
            // net effect was a primary, Tab-reachable control with no focus indicator at all
            // (`110` §11). Omitting it lets `theme.css`'s global `:focus-visible` rule draw the
            // 2px ring — on keyboard focus only, so the chromeless look survives a mouse click.
            className="min-w-0 flex-1 border-none bg-transparent text-2xl font-semibold placeholder:text-[var(--muted-foreground)]"
          />

          {/* `070` §8: one boolean on a page, optimistic, no dedicated endpoint. Filled and
              amber when on; outline and muted when off — and `aria-pressed` says so too,
              because no state in this app is carried by colour alone (`110` §1). */}
          <button
            type="button"
            data-testid="favorite-toggle"
            aria-label={
              favorite
                ? t('notebook.removeFromFavoritesHeader')
                : t('notebook.addToFavoritesHeader')
            }
            aria-pressed={favorite}
            title={
              favorite
                ? t('notebook.removeFromFavoritesHeader')
                : t('notebook.addToFavoritesHeader')
            }
            onClick={() => {
              update.mutate({ id: pageId, isFavorite: !favorite });
            }}
            className="flex size-7 shrink-0 items-center justify-center rounded-sm hover:bg-[var(--accent)]"
          >
            <Star
              className={cn(
                'size-4',
                favorite ? 'text-[var(--warning)]' : 'text-[var(--muted-foreground)]',
              )}
              fill={favorite ? 'currentColor' : 'none'}
              strokeWidth={1.75}
            />
          </button>
        </div>

        {/* Tags live in the page header — "there is no 'manage tags' step before you can use
            one" (`070` §6). Only once the server has told us what they are: an optimistic
            chip list built before the page has loaded would be a list of nothing. */}
        {server !== undefined && !archived && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {/* `?? []` is not defensive padding, it is the OFFLINE path: a page rendered from
                a draft has no server copy, and a page fetched from an older backend has no
                `tags` key at all. Neither is a reason to take the editor down. */}
            <TagInput pageId={pageId} tags={server.tags ?? []} />

            {/* "I need to prep this page before Tuesday" (`080` §7). A deadline ON THE PAGE —
                it is not an event, and it never becomes one. */}
            <DueDateButton
              pageId={pageId}
              dueAt={server.dueAt}
              dueAllDay={server.dueAllDay ?? true}
            />
          </div>
        )}

        {archived && <p className="mt-1 text-[var(--warning)]">{t('notebook.archivedNotice')}</p>}
        {boot.offline && (
          <p className="mt-1 text-[var(--warning)]">{t('notebook.offlineNotice')}</p>
        )}
      </header>

      {conflict !== undefined && (
        <RecoveryBanner conflict={conflict} onKeepMine={keepMine} onUseServer={useServer} />
      )}

      <Editor
        key={`${pageId}:${String(boot.revision)}`}
        pageId={pageId}
        title={title}
        content={boot.content.content === undefined ? emptyDoc() : boot.content}
        // The LATEST `updatedAt` the server has told us about — not the boot-time one.
        // Every successful save advances it (OutboxBridge writes the PATCH response into
        // the detail cache), and the next save must use the new value or it would be
        // stale against the write we ourselves just made. Falls back to the boot value
        // when there is no server copy at all — i.e. we are offline on a draft.
        baseUpdatedAt={server?.updatedAt ?? boot.baseUpdatedAt}
        editable={editable}
        save={save}
        autoFocus={focusEditor}
        onOpenConflict={() => {
          // The indicator is clickable in the conflict state; the banner is already the
          // only way to clear it, and it is already on screen.
        }}
        onCreateLinkedPage={createLinkedPage}
        onGatherChildren={gatherChildren}
        // In the document flow, below the editor, inside the same scroll container — NOT in
        // a right-hand drawer (`070` §7.6). It is part of the page's meaning.
        //
        // The event links sit BESIDE the backlinks (`080` §8): "which pages mention him" and
        // "which sessions he is scheduled to appear in" are the same kind of answer, and they
        // belong next to each other.
        footer={
          <>
            <BacklinksPanel pageId={pageId} onCreatePage={createLinkedPage} />
            <EventLinksPanel pageId={pageId} />
          </>
        }
      />
    </div>
  );
}
